/** Shared helpers for stack ranking per-skill scores (hub + results view). */

export function scoreColor(n) {
  if (n >= 60) return '#10B981';
  if (n >= 40) return '#F59E0B';
  if (n > 0) return '#EF4444';
  return 'var(--ds-muted2, #C8D0E0)';
}

export function parseParameterScores(raw) {
  if (raw == null) return null;
  if (Array.isArray(raw)) return normalizeParameterScoresMap(raw);
  if (typeof raw === 'object') return normalizeParameterScoresMap(raw);
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    try {
      const parsed = JSON.parse(trimmed);
      return normalizeParameterScoresMap(parsed);
    } catch {
      return null;
    }
  }
  return null;
}

/** Normalize dict or [{parameter, score}] into a flat name→score map. */
export function normalizeParameterScoresMap(raw) {
  if (raw == null) return null;
  if (Array.isArray(raw)) {
    const out = {};
    for (const item of raw) {
      if (!item || typeof item !== 'object') continue;
      const name =
        item.parameter ||
        item.name ||
        item.skillName ||
        item.skill ||
        item.label;
      const val =
        item.score ??
        item.value ??
        item.weighted_score ??
        item.percentage;
      if (name == null || val == null) continue;
      const score = parseScoreValue(val);
      if (score != null) out[String(name)] = score;
    }
    return Object.keys(out).length > 0 ? out : null;
  }
  if (typeof raw === 'object') {
    const keys = Object.keys(raw);
    if (keys.length === 0) return null;
    const out = {};
    for (const [k, v] of Object.entries(raw)) {
      const score = parseScoreValue(v);
      if (score != null) out[k] = score;
    }
    return Object.keys(out).length > 0 ? out : null;
  }
  return null;
}

function normalizeParamKey(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function parseScoreValue(val) {
  if (val == null || val === '') return null;
  const n = Number(val);
  if (!Number.isNaN(n)) {
    if (n > 0 && n <= 1) return Math.round(n * 100);
    return Math.round(n);
  }
  const m = String(val).match(/(\d+(?:\.\d+)?)/);
  return m ? Math.round(Number(m[1])) : null;
}

function scoreForSkillName(ps, skillName) {
  if (!ps || !skillName) return null;
  const target = normalizeParamKey(skillName);
  if (!target) return null;

  let exact = null;
  let fuzzy = null;

  for (const [k, v] of Object.entries(ps)) {
    const norm = normalizeParamKey(k);
    if (!norm) continue;
    const num = parseScoreValue(v);
    if (num == null) continue;
    if (norm === target) return num;
    if (!exact && (norm.includes(target) || target.includes(norm))) {
      fuzzy = num;
    }
  }
  return exact ?? fuzzy;
}

/** Default JD scoring parameters (matches backend/Python fallback for experienced roles). */
export const DEFAULT_SKILL_WEIGHTAGES = [
  { skillName: 'Technical Skills Match', weightage: 25 },
  { skillName: 'Relevant Experience', weightage: 20 },
  { skillName: 'Educational Background', weightage: 15 },
  { skillName: 'Industry Experience', weightage: 15 },
  { skillName: 'Project Experience', weightage: 15 },
  { skillName: 'Communication Skills', weightage: 10 },
];

/** Generic intern/trainee defaults — Industry Experience is not applicable. */
export const INTERN_SKILL_WEIGHTAGES = [
  { skillName: 'Technical Skills Match', weightage: 30 },
  { skillName: 'Educational Background', weightage: 20 },
  { skillName: 'Academic / Project Experience', weightage: 25 },
  { skillName: 'Problem Solving & Learning Agility', weightage: 15 },
  { skillName: 'Communication Skills', weightage: 10 },
];

/** AI / ML intern — domain-specific; no Industry Experience. */
export const AI_INTERN_SKILL_WEIGHTAGES = [
  { skillName: 'AI / ML Fundamentals', weightage: 30 },
  { skillName: 'Educational Background', weightage: 20 },
  { skillName: 'Academic / Project Experience', weightage: 25 },
  { skillName: 'Problem Solving & Learning Agility', weightage: 15 },
  { skillName: 'Communication Skills', weightage: 10 },
];

export function isInternOrTraineeTitle(title) {
  return /\b(interns?|internship|trainee)\b/i.test(String(title || ''));
}

function normalizeTitleTokens(title) {
  return String(title || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Early-career titles (junior / fresher / intern / entry-level).
 * Used only as an AI guardrail signal — skill names/weights come from AI, not hardcoded lists.
 */
export function isJuniorOrEntryLevelTitle(title) {
  const norm = normalizeTitleTokens(title);
  if (!norm) return false;
  if (isInternOrTraineeTitle(title)) return true;
  return /\b(junior|jr|fresher|freshers|entry\s*level|entrylevel|graduate|campus\s*hire)\b/.test(norm);
}

/** True when Industry Experience must not appear (AI + safety strip). */
export function shouldOmitIndustryExperience(title) {
  return isJuniorOrEntryLevelTitle(title) || isInternOrTraineeTitle(title);
}

/**
 * Emergency fallback only when AI is unavailable.
 * Prefer AI-generated skills via ensureRoleSkillWeightages / generate-scoring-parameters.
 */
export function getDefaultSkillWeightagesForTitle(title) {
  const copy = (rows) => rows.map((s) => ({ ...s }));
  if (isInternOrTraineeTitle(title) || isJuniorOrEntryLevelTitle(title)) {
    return copy(INTERN_SKILL_WEIGHTAGES);
  }
  return copy(DEFAULT_SKILL_WEIGHTAGES);
}

function skillLabel(skill) {
  return String(skill?.skillName || skill?.skill || skill?.parameter || skill?.name || '').trim();
}

function skillWeight(skill) {
  return Math.max(0, Number(skill?.weightage ?? skill?.weight) || 0);
}

/**
 * For intern/trainee/junior/entry-level roles, drop Industry Experience and
 * redistribute its weight so totals stay at 100%.
 * Preserves input shape: {skill,weight} or {skillName,weightage}.
 */
export function stripIndustryExperienceForInterns(skills, title) {
  if (!Array.isArray(skills) || skills.length === 0 || !shouldOmitIndustryExperience(title)) {
    return skills;
  }

  const useSkillKey = skills.some((s) => s.skill != null && s.skillName == null);
  const kept = [];
  let removed = 0;

  for (const skill of skills) {
    const name = skillLabel(skill);
    const weight = skillWeight(skill);
    if (/industry\s*experience/i.test(name)) {
      removed += weight;
      continue;
    }
    kept.push(useSkillKey ? { skill: name, weight } : { skillName: name, weightage: weight });
  }

  if (kept.length === 0) {
    return getDefaultSkillWeightagesForTitle(title).map((s) =>
      useSkillKey ? { skill: s.skillName, weight: s.weightage } : { ...s }
    );
  }
  if (removed <= 0) return skills;

  const boostOrder = ['technical', 'education', 'project', 'ai', 'ml', 'academic', 'problem', 'relevant', 'communication'];
  const rank = (s) => {
    const key = skillLabel(s).toLowerCase();
    const idx = boostOrder.findIndex((t) => key.includes(t));
    return idx === -1 ? boostOrder.length : idx;
  };
  const order = kept
    .map((s, i) => ({ i, r: rank(s) }))
    .sort((a, b) => a.r - b.r || a.i - b.i);

  let rem = removed;
  let k = 0;
  while (rem > 0 && order.length) {
    const idx = order[k % order.length].i;
    if (useSkillKey) kept[idx].weight += 1;
    else kept[idx].weightage += 1;
    rem -= 1;
    k += 1;
  }

  return kept;
}

export function mapScoringParametersToSkills(params) {
  if (!Array.isArray(params)) return [];
  return params
    .map((p) => ({
      skillName: p.parameter || p.skillName || p.name,
      weightage: Number(p.weight ?? p.weightage) || 0,
    }))
    .filter((sk) => sk.skillName);
}

/** Resolve skill list for breakdown UI — never return empty when we can infer categories. */
export function resolveEffectiveSkills(skills, candidate) {
  const mapped = mapScoringParametersToSkills(skills);
  if (mapped.length > 0) return mapped;

  const ps = resolveParameterScoresFromCandidate(candidate);
  if (ps && Object.keys(ps).length > 0) {
    return Object.keys(ps).map((skillName) => ({ skillName, weightage: 0 }));
  }

  return DEFAULT_SKILL_WEIGHTAGES;
}

const PLACEHOLDER_VALUES = new Set([
  'not specified',
  'not provided',
  'not analyzed',
  'not processed',
  'unknown',
  'n/a',
]);

export function candidateDisplayName(candidate) {
  if (!candidate) return 'Candidate';
  const options = [
    candidate.candidateName,
    candidate.candidate_name,
    candidate.name,
  ];
  for (const raw of options) {
    const val = String(raw || '').trim();
    if (val && !PLACEHOLDER_VALUES.has(val.toLowerCase())) return val;
  }
  return 'Candidate';
}

function parseAnalysisObject(candidate) {
  const raw = candidate?.aiAnalysis ?? candidate?.ai_analysis;
  if (!raw) return {};
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return typeof parsed === 'object' && parsed ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
}

const SKILL_CATEGORY_HINTS = {
  technical: [
    'technical', 'programming', 'coding', 'software', 'developer', 'framework',
    'language', 'api', 'database', 'javascript', 'python', 'java', 'php',
    'laravel', 'react', 'angular', 'sql', 'cloud', 'aws', 'azure', 'devops', 'git',
  ],
  relevant: [
    'experience', 'years', 'role', 'position', 'worked', 'background', 'tenure', 'career',
  ],
  education: [
    'education', 'degree', 'bachelor', 'master', 'graduation', 'university',
    'college', 'diploma', 'b.tech', 'mba', 'gpa', 'qualification',
  ],
  industry: [
    'industry', 'domain', 'sector', 'vertical', 'healthcare', 'finance',
    'banking', 'retail', 'ecommerce', 'enterprise', 'fintech',
  ],
  project: [
    'project', 'delivered', 'implementation', 'deployment', 'built',
    'developed', 'launch', 'initiative',
  ],
  communication: [
    'communication', 'collaboration', 'agile', 'team', 'stakeholder',
    'presentation', 'leadership', 'interpersonal', 'scrum',
  ],
};

function categoryForSkillName(skillName) {
  const key = normalizeParamKey(skillName);
  if (key.includes('technical')) return 'technical';
  if (key.includes('communication')) return 'communication';
  if (key.includes('education')) return 'education';
  if (key.includes('industry')) return 'industry';
  if (key.includes('project')) return 'project';
  if (key.includes('relevant')) return 'relevant';
  if (key.includes('experience') && !key.includes('project') && !key.includes('industry')) {
    return 'relevant';
  }
  return 'relevant';
}

function countHintHits(text, hints) {
  let hits = 0;
  for (const hint of hints) {
    if (text.includes(hint)) hits += 1;
  }
  return hits;
}

function tokenizeSkillsList(text) {
  return String(text || '')
    .split(/[,;\n|]+/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * Estimate per-parameter scores from match_summary / matching_skills when DB has no parameter_scores.
 */
export function inferParameterScoresFromAnalysis(candidate, skills) {
  const total = candidateTotalScore(candidate);
  if (total <= 0) return null;

  const analysis = parseAnalysisObject(candidate);
  const matching = String(
    analysis.matching_skills ||
      analysis.matchingSkills ||
      analysis.skills ||
      candidate.matching_skills ||
      candidate.matchingSkills ||
      candidate.skills ||
      '',
  ).toLowerCase();
  const missing = String(
    analysis.missing_skills ||
      analysis.missingSkills ||
      candidate.missing_skills ||
      candidate.missingSkills ||
      '',
  ).toLowerCase();
  const summary = String(
    analysis.match_summary ||
      analysis.matchSummary ||
      analysis.summary ||
      candidate.match_summary ||
      candidate.matchSummary ||
      '',
  ).toLowerCase();
  const strengths = String(analysis.strengths || candidate.strengths || '').toLowerCase();
  const concerns = String(analysis.concerns || candidate.concerns || '').toLowerCase();
  const education = String(
    analysis.education || candidate.education || '',
  ).toLowerCase();
  const experience = String(
    analysis.experience || candidate.experience || '',
  ).toLowerCase();
  const combined = `${matching} ${missing} ${summary} ${strengths} ${concerns}`;
  const matchingTokens = tokenizeSkillsList(matching);
  const missingTokens = tokenizeSkillsList(missing);

  const effectiveSkills = skills?.length > 0 ? skills : DEFAULT_SKILL_WEIGHTAGES;

  const buckets = effectiveSkills.map((sk) => {
    const name = sk.skillName || sk.parameter || 'Skill';
    const cat = categoryForSkillName(name);
    const hints = SKILL_CATEGORY_HINTS[cat] || SKILL_CATEGORY_HINTS.relevant;

    let pos = countHintHits(matching, hints) + countHintHits(strengths, hints);
    let neg = countHintHits(missing, hints) + countHintHits(concerns, hints);

    if (cat === 'education') {
      if (education.includes('bachelor') || education.includes('master') || education.includes('degree')) {
        pos += 2;
      }
      if (combined.includes('no degree') || combined.includes('education gap')) neg += 2;
    }
    if (cat === 'relevant') {
      const expMatch = experience.match(/(\d+(?:\.\d+)?)/);
      if (expMatch) {
        const yrs = Number(expMatch[1]);
        if (yrs >= 5) pos += 2;
        else if (yrs >= 2) pos += 1;
        else neg += 1;
      }
      if (combined.includes('less than') || combined.includes('insufficient experience')) neg += 2;
    }
    if (cat === 'technical') {
      pos += Math.min(3, matchingTokens.length * 0.5);
      neg += Math.min(4, missingTokens.length * 0.7);
    }

    let ratio = 0.42;
    if (pos + neg > 0) {
      ratio = (pos + 1.2) / (pos + neg + 2.4);
    }
    if (total < 45) ratio = Math.min(ratio, 0.72);
    if (total >= 60) ratio = Math.max(ratio, 0.35);

    return {
      name,
      ratio,
      weight: Number(sk.weightage ?? sk.weight) || 1,
    };
  });

  const totalWeight = buckets.reduce((s, b) => s + b.weight, 0) || buckets.length;
  const rAvg =
    buckets.reduce((s, b) => s + b.ratio * b.weight, 0) / totalWeight || 1;

  const out = {};
  for (const b of buckets) {
    const score = Math.round(total * (b.ratio / rAvg));
    out[b.name] = Math.max(0, Math.min(100, score));
  }
  return Object.keys(out).length > 0 ? out : null;
}

/** Resolve parameter_scores from API fields or embedded aiAnalysis JSON. */
export function resolveParameterScoresFromCandidate(candidate, skills) {
  if (!candidate) return null;

  let ps =
    normalizeParameterScoresMap(candidate.parameter_scores) ||
    normalizeParameterScoresMap(candidate.parameterScores);
  if (ps) return ps;

  const rawAnalysis = candidate.aiAnalysis ?? candidate.ai_analysis;
  if (rawAnalysis) {
    let parsed = rawAnalysis;
    if (typeof rawAnalysis === 'string') {
      try {
        parsed = JSON.parse(rawAnalysis);
      } catch {
        parsed = null;
      }
    }
    if (parsed && typeof parsed === 'object') {
      ps =
        normalizeParameterScoresMap(parsed.parameter_scores) ||
        normalizeParameterScoresMap(parsed.parameterScores) ||
        normalizeParameterScoresMap(parsed.scoring_parameters);
      if (ps) return ps;
    }
  }

  const legacy = legacyScoresAsMap(candidate);
  if (legacy) return legacy;

  return inferParameterScoresFromAnalysis(candidate, skills);
}

function legacyScoresAsMap(candidate) {
  const legacy = legacyBreakdownFromColumns(candidate);
  if (!legacy.length) return null;
  return Object.fromEntries(legacy.map((row) => [row.name, row.score]));
}

/** Merge API candidate with parsed aiAnalysis fields (hub + list views). */
export function enrichStackRankingCandidate(raw, skills) {
  if (!raw) return raw;
  let ps = resolveParameterScoresFromCandidate(raw, skills);
  if (!ps) {
    ps = legacyScoresAsMap(raw);
  }
  return {
    ...raw,
    parameter_scores: ps || raw.parameter_scores || raw.parameterScores,
    parameterScores: ps || raw.parameterScores || raw.parameter_scores,
    aiAnalysis: raw.aiAnalysis ?? raw.ai_analysis,
    ai_analysis: raw.ai_analysis ?? raw.aiAnalysis,
  };
}

export function candidateTotalScore(candidate) {
  if (!candidate) return 0;
  const raw =
    candidate.totalScore ??
    candidate.total_score ??
    candidate.score ??
    candidate.match_score ??
    candidate.matchScore;
  const n = Number(raw);
  return Number.isFinite(n) ? Math.round(n) : 0;
}

function legacyBreakdownFromColumns(candidate) {
  const pairs = [
    ['Technical Skills', candidate.technicalScore ?? candidate.technical_score],
    ['Communication', candidate.communicationScore ?? candidate.communication_score],
    ['Experience', candidate.experienceScore ?? candidate.experience_score],
    ['Skills', candidate.skillsScore ?? candidate.skills_score],
  ];
  return pairs
    .map(([name, val]) => ({ name, score: parseScoreValue(val), weight: 0 }))
    .filter((row) => row.score != null);
}

/**
 * Build per-skill breakdown rows for UI bars.
 * @param {object} candidate
 * @param {Array<{skillName?: string, parameter?: string, weightage?: number}>} skills
 */
export function buildSkillScoreBreakdown(candidate, skills) {
  const ps = resolveParameterScoresFromCandidate(candidate, skills);
  const effectiveSkills = resolveEffectiveSkills(skills, candidate);

  const mapped = effectiveSkills.map((sk) => {
    const name = sk.skillName || sk.parameter || 'Skill';
    let score = 0;
    if (ps) {
      const matched = scoreForSkillName(ps, name);
      if (matched != null) score = matched;
    }
    return {
      name,
      score,
      weight: Number(sk.weightage ?? sk.weight) || 0,
    };
  });

  const hasPs = ps && Object.keys(ps).length > 0;
  const allZero = mapped.length > 0 && mapped.every((row) => row.score === 0);

  if (hasPs && allZero) {
    const fromPs = Object.entries(ps)
      .map(([name, val]) => ({ name, score: parseScoreValue(val), weight: 0 }))
      .filter((row) => row.score != null);
    if (fromPs.length > 0) return fromPs;
  }

  if (mapped.some((row) => row.score > 0)) return mapped;

  const legacy = legacyBreakdownFromColumns(candidate);
  if (legacy.length > 0) return legacy;

  return mapped;
}
